import React, { useEffect, useState, useRef } from "react";
import { Button, Card, Col, Form, Input, Row, Select, SelectProps } from "antd";
import { useParams } from "react-router-dom";
import { Toast } from 'primereact/toast';
import HTTPService from "../../../utils/makerequest";
import { ICompanyData } from "../../../interfaces/ICompanyData";
import { IAuthentication, UserRole } from "../../../interfaces/authentication";
import { StateLoader } from "../../../redux/StateLoader";
import { ICategoryData } from "../../../interfaces/ICategoryData";
import { useTranslation } from 'react-i18next'; 
import { IPageProps } from "../../../interfaces/page-data";
import { SyncOutlined, CloseOutlined } from "@ant-design/icons";

const UpdateCategory: React.FunctionComponent<IPageProps> = () => {
  const { categoryId } = useParams<any>();
  const toast = useRef<any>(null);
  const [categoryData, setCategoryData] = useState<ICategoryData>();
  const [companyData, setCompanyData] = useState<ICompanyData[]>();
  const loader = new StateLoader();
  const userInfo: IAuthentication = loader.loadAuthState();
  const { t } = useTranslation();

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (categoryId != null) {
      getCategoryData(categoryId);
    } else if (categoryData?.categoryId) {
      getCategoryData(categoryData.categoryId);
    }
  }, [categoryId, categoryData?.categoryId]);

  const fetchData = async () => {
    try {
      const result = await HTTPService.GETCompanyNames();
      setCompanyData(result);
    } catch (error) {
      console.error("Error fetching company data:", error);
    }
  };

  const filterOption: SelectProps<any>['filterOption'] = (input, option) => {
    return (option?.children as unknown as string).toLowerCase().includes(input.toLowerCase());
  };

  const getCategoryData = (categoryId: number) => {
    HTTPService.GET(`/Category/ById?categoryId=${categoryId}`)
      .then((res) => {
        if (res.status === 200) {
          setCategoryData(res.data);
        } else {
          toast.current?.show({
            severity: 'error',
            summary: t('mistake'),
            detail: res.error.message,
            life: 3000
          });
        }
      })
      .catch((error) => {
        console.error("Error fetching category data:", error);
      });
  };

  const updateCategory = (formData) => {
    const updatedCategory = {
      categoryId: categoryId,
      categoryName: formData.categoryName,
      companyId: formData.companyId || userInfo?.companyId,
    };

    HTTPService.PUT(`/Category/updateCategory`, updatedCategory)
      .then((res) => {
        if (res.status === 200) {
          toast.current?.show({
            severity: 'success',
            summary: t('successful'),
            detail: t('category_updated_successfully'),
            life: 3000
          });
          window.location.replace("/admin/category-management");
        } else if(res.status === 450) {
          toast.current?.show({
            severity: 'error',
            summary: t('mistake'),
            detail: t(res.data),
            life: 3000
          });
        } else {
          toast.current?.show({
            severity: 'error',
            summary: t('mistake'),
            detail: t(res.data),
            life: 3000
          });
        }
      })
      .catch((error) => {
        toast.current?.show({
          severity: 'error',
          summary: t('mistake'),
          detail: t('error_updating_category'),
          life: 3000
        });
      });
  };

  const handleCancel = () => {
    window.location.replace("/admin/category-management/");
  };

  const { Option } = Select;

  return (
    <>
      <Toast ref={toast} />
      <Form
        layout="vertical"
        onFinish={updateCategory}
        fields={[
          { name: ["categoryName"], value:categoryData?.categoryName },
          { name: ["companyId"], value:categoryData?.companyId },
        ]}
      >
        <Card style={{ padding: "50px" }}>
          <h3>{t('update_category')}</h3>
          {userInfo?.role === UserRole.AltisAdmin ? (
            <Form.Item
              name='companyId'
              label={t('Select company')}
              rules={[
                {
                  required: true,
                  message: t('please_select_a_company'),
                },
              ]}
            >
              <Select showSearch placeholder={t('company_name')} style={{ width: '100%' }} filterOption={filterOption}>
                {companyData?.map((company) => (
                  <Select.Option key={company.companyId} value={company.companyId}>
                    {company.companyName}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          ) : null}
          <Row gutter={16}>
            <Col xs={24} sm={24} md={24} lg={24}>
              <Form.Item
                name="categoryName"
                label={t('category_name')}
                rules={[
                  {
                    required: true,
                    message: t('please_input_your_category_name'),
                    whitespace: true,
                  },
                ]}
              >
                <Input style={{ width: '100%',borderRadius: '0' }} placeholder={t('category_name')} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item>
            <Row gutter={[4, 4]} justify="end" align="middle">
              <Col xs={12} sm={4}>
                <Button
                  block
                  onClick={handleCancel}
                  type="primary"
                  style={{
                    height: 64,
                    backgroundColor: 'transparent',
                    color: '#1890ff',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '0 10px',
                    border: 'none',
                    transition: 'all 0.3s ease',
                    cursor: 'pointer'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'scale(1.05)';
                    e.currentTarget.style.boxShadow = '0 8px 16px rgba(0,0,0,0.2)';
                    e.currentTarget.style.color = '#40a9ff';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'scale(1)';
                    e.currentTarget.style.boxShadow = 'none';
                    e.currentTarget.style.color = '#1890ff';
                  }}
                >
                  <CloseOutlined style={{ fontSize: 24 }} />
                  <span style={{ fontSize: '12px', marginTop: '8px', textAlign: 'center' }}>
                    {t('cancel')}
                  </span>
                </Button>
              </Col>
              <Col xs={12} sm={4}>
                <Button
                  block
                  htmlType="submit"
                  type="primary"
                  style={{
                    height: 64,
                    backgroundColor: 'transparent',
                    color: '#1890ff',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '0 10px',
                    border: 'none',
                    transition: 'all 0.3s ease',
                    cursor: 'pointer'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'scale(1.05)';
                    e.currentTarget.style.boxShadow = '0 8px 16px rgba(0,0,0,0.2)';
                    e.currentTarget.style.color = '#40a9ff';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'scale(1)';
                    e.currentTarget.style.boxShadow = 'none';
                    e.currentTarget.style.color = '#1890ff';
                  }}
                >
                  <SyncOutlined style={{ fontSize: 24 }} />
                  <span style={{ fontSize: '12px', marginTop: '8px', textAlign: 'center' }}>
                    {t('update')}
                  </span>
                </Button>
              </Col>
            </Row>
          </Form.Item>
        </Card>
      </Form>
    </>
  );
};

export default UpdateCategory;
